// Cliente HTTP del módulo de cobros recurrentes — etapas E1 y E2.
//
// SPEC_CORE_COBROS_RECURRENTES_v0.1.md sección 8 (contratos)
//
// Una sola capa entre las vistas y el API: si mañana cambia una ruta, se toca
// acá y no en tres pantallas. Reusa `apiService`, que ya inyecta el token y
// normaliza los errores contra el estándar del repo (`errorContract.js`), así
// que el `situation` que devuelve el backend llega intacto a la vista.

import apiService from './apiService';

const datos = (respuesta) => respuesta?.data?.data ?? null;

// OJO con la firma: el wrapper del repo es `get(url, params, options)` y el
// SEGUNDO argumento son los params, no una config de axios. Pasarle
// `{ params: {...} }` —que es la forma de axios crudo— los manda anidados y el
// backend no ve ningun filtro: la consulta devuelve todo. Se descubrio en el
// VERIFY de CR-E2-006, y afectaba tambien a las llamadas de E1.

/* --- Esquemas de cobro ---------------------------------------------------- */

// Devuelve `{ esquema, periodos }`. Los períodos vienen calculados por el
// backend, no por la UI: es la misma función pura que usa la generación, así
// que lo que el administrador previsualiza es exactamente lo que se va a
// generar.
export async function crearEsquema(payload) {
  return datos(await apiService.post('/api/esquemas', payload));
}

export async function listarEsquemas({ activo = true } = {}) {
  const params = activo === null ? {} : { activo: String(activo) };
  return datos(await apiService.get('/api/esquemas', params)) || [];
}

// Devuelve `{ esquema, periodos, editable }`. `editable` lo decide el backend
// —es regla de dominio, no de pantalla—: un esquema deja de ser configuración
// y pasa a ser registro histórico en cuanto genera su primera cuota.
export async function obtenerEsquema(id) {
  return datos(await apiService.get(`/api/esquemas/${id}`));
}

// Corrige el calendario y los montos MIENTRAS el esquema no haya generado
// nada. Después el backend responde 409 `esquema_con_deudas_generadas`.
export async function actualizarEsquema(id, payload) {
  return datos(await apiService.patch(`/api/esquemas/${id}`, payload));
}

// Duplicar para el período siguiente: misma forma y mismos conceptos con sus
// montos, calendario nuevo. Es lo que hace reutilizable un esquema sin
// reescribir la historia del anterior.
export async function duplicarEsquema(id, payload) {
  return datos(await apiService.post(`/api/esquemas/${id}/duplicar`, payload));
}

export async function actualizarConcepto(conceptoId, payload) {
  return datos(await apiService.patch(`/api/esquemas/conceptos/${conceptoId}`, payload));
}

/* --- Editar y eliminar conceptos (D45) ------------------------------------ */
//
// SPEC_EDICION_DE_CONCEPTOS_v0.1.md · ZC-F5-001. Dos puertas con el mismo
// contrato (PZ-5): las cuotas y matrículas van por `/api/esquemas/conceptos`,
// los cargos eventuales por `/api/cobranza/cargos`. La puerta la decide el
// TIPO del concepto, no la pantalla: así el gerente, que sólo tiene el permiso
// de cargos, usa los mismos modales.
const baseDeConcepto = (concepto) => (concepto.tipo === 'EVENTUAL'
  ? `/api/cobranza/cargos/${concepto.cr_esquema_concepto_id}`
  : `/api/esquemas/conceptos/${concepto.cr_esquema_concepto_id}`);

// Previsualizar NO escribe: devuelve qué cuotas cambian, las vencidas aparte
// y las que no se tocan con su motivo (RN-161).
export async function previsualizarEdicionConcepto(concepto, payload) {
  return datos(await apiService.post(`${baseDeConcepto(concepto)}/edicion/previsualizar`, payload));
}

// Vuelve a calcular del lado del servidor (RN-164): entre que el operador miró
// y confirmó, otro pudo cobrar una de esas cuotas.
export async function confirmarEdicionConcepto(concepto, payload) {
  return datos(await apiService.post(`${baseDeConcepto(concepto)}/edicion/confirmar`, payload));
}

export async function previsualizarEliminacionConcepto(concepto) {
  return datos(await apiService.post(`${baseDeConcepto(concepto)}/eliminacion/previsualizar`, {}));
}

export async function confirmarEliminacionConcepto(concepto, payload = {}) {
  return datos(await apiService.post(`${baseDeConcepto(concepto)}/eliminacion/confirmar`, payload));
}

// Desactivar (RN-171): deja de generarse para los nuevos; lo emitido sigue.
export async function desactivarConceptoPorTipo(concepto) {
  return datos(await apiService.delete(baseDeConcepto(concepto)));
}

// Reactivar (RN-181, SPEC §14): la inversa. No toca ninguna cuota emitida.
export async function reactivarConceptoPorTipo(concepto) {
  return datos(await apiService.post(`${baseDeConcepto(concepto)}/reactivar`, {}));
}

/* --- Suscripciones -------------------------------------------------------- */

export async function listarSuscripciones(filtros = {}) {
  // Sólo se mandan los filtros con valor: un `estado=` vacío en la query
  // string llega como string vacío y no como "sin filtro".
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return datos(await apiService.get('/api/suscripciones', params)) || [];
}

export async function obtenerSuscripcion(id) {
  return datos(await apiService.get(`/api/suscripciones/${id}`));
}

// El alta manda la suscripción Y sus titulares en una sola llamada: el backend
// las crea en la misma transacción porque una suscripción sin responsable de
// pago no se le puede cobrar a nadie (RN-3).
export async function crearSuscripcion(payload) {
  return datos(await apiService.post('/api/suscripciones', payload));
}

export async function actualizarSuscripcion(id, payload) {
  return datos(await apiService.patch(`/api/suscripciones/${id}`, payload));
}

/* --- Titulares ------------------------------------------------------------ */

export async function agregarTitular(suscripcionId, payload) {
  return datos(await apiService.post(`/api/suscripciones/${suscripcionId}/titulares`, payload));
}

// No borra el vínculo: le cierra la vigencia. La titularidad es historia —cada
// cuota conserva el responsable con el que nació (RN-5)— y borrarla dejaría
// deudas apuntando a un pasado irreconstruible.
export async function cerrarTitular(titularId, payload = {}) {
  return datos(await apiService.delete(`/api/suscripciones/titulares/${titularId}`, { data: payload }));
}

/* --- Conceptos del esquema (E2, RN-9) ------------------------------------- */

// Qué se cobra en cada período. Un concepto NO es un producto (D15): lleva su
// propio `tipo_iva` y no toca el catálogo.
export async function listarConceptos(esquemaId) {
  return datos(await apiService.get(`/api/esquemas/${esquemaId}/conceptos`)) || [];
}

export async function crearConcepto(esquemaId, payload) {
  return datos(await apiService.post(`/api/esquemas/${esquemaId}/conceptos`, payload));
}

// No borra: desactiva. Un concepto borrado dejaría deudas ya generadas
// apuntando a un concepto inexistente, y la deuda es la verdad (artículo 5).
export async function desactivarConcepto(conceptoId) {
  return datos(await apiService.delete(`/api/esquemas/conceptos/${conceptoId}`));
}

/* --- Descuentos y becas (E2, RN-13 a RN-16) ------------------------------- */

export async function listarDescuentos(suscripcionId) {
  return datos(await apiService.get(`/api/suscripciones/${suscripcionId}/descuentos`)) || [];
}

export async function crearDescuento(suscripcionId, payload) {
  return datos(await apiService.post(`/api/suscripciones/${suscripcionId}/descuentos`, payload));
}

// Quitar un descuento SIN recalcular. Sigue existiendo para el caso en que no
// hay nada que tocar —una suscripción recién creada, sin cuotas—.
//
// **El comentario que estaba acá decía «Afecta a los períodos que vengan». No
// afectaba a ninguno**, y era la misma mentira que la pantalla le contaba al
// operador: la beca se cargaba, se veía listada, y no descontaba nada. Para que
// alcance a las cuotas está el par de abajo.
//
// Pide motivo: quitar un descuento SUBE importes (artículo 6).
export async function eliminarDescuento(descuentoId, motivo) {
  // El wrapper ya envuelve en `{ data }`: acá va el cuerpo pelado.
  return datos(await apiService.delete(
    `/api/suscripciones/descuentos/${descuentoId}`, { motivo }));
}

/* --- Recálculo de descuentos, en dos pasos (DA-F5, RN-73, D39) ------------ */

// Qué cambiaría. NO escribe: es lo que el operador mira antes de decidir.
export async function previsualizarDescuentos(suscripcionId, payload) {
  return datos(await apiService.post(
    `/api/suscripciones/${suscripcionId}/descuentos/previsualizar`, payload));
}

// Aplica. Vuelve a calcular del lado del servidor (RN-76): entre que el
// operador miró y confirmó, otro pudo cobrar una de esas cuotas.
export async function confirmarDescuentos(suscripcionId, payload) {
  return datos(await apiService.post(
    `/api/suscripciones/${suscripcionId}/descuentos/confirmar`, payload));
}

// RN-79: si hay un descuento cargado que no está aplicado en las cuotas, se ve
// **al abrir la pantalla**, sin que nadie pregunte. Era el defecto de fondo:
// dos paneles contando cosas distintas y nada que dijera cuál mentía.
export async function estadoDescuentos(suscripcionId) {
  return datos(await apiService.get(
    `/api/suscripciones/${suscripcionId}/descuentos/estado`));
}

/* --- Generación de deudas (E2, RN-17, RN-18, AMB-6) ----------------------- */

// Dos pasos y no uno, a propósito: generar un período crea plata a cobrar para
// cientos de personas de una vez. `previsualizar` NO escribe nada — devuelve
// el resumen y una muestra de hasta 10 filas.
export async function previsualizarGeneracion(payload) {
  return datos(await apiService.post('/api/cobranza/generacion/previsualizar', payload));
}

export async function confirmarGeneracion(payload) {
  return datos(await apiService.post('/api/cobranza/generacion/confirmar', payload));
}

/* --- Deudas --------------------------------------------------------------- */

export async function listarDeudas(filtros = {}) {
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return datos(await apiService.get('/api/cobranza/deudas', params)) || [];
}

/* --- Baja y reasignación (E3) --------------------------------------------- */

// Dar de baja se previsualiza antes de confirmar, igual que la generación
// (AMB-6): puede anular nueve cuotas de una sola vez.
export async function previsualizarBaja(suscripcionId, payload) {
  return datos(await apiService.post(
    `/api/suscripciones/${suscripcionId}/baja`, { ...payload, previsualizar: true }));
}

export async function darDeBaja(suscripcionId, payload) {
  return datos(await apiService.post(`/api/suscripciones/${suscripcionId}/baja`, payload));
}

// Relevo del responsable: cerrar al anterior y abrir al nuevo en UNA
// operación. Con dos llamadas es imposible — el índice único obliga a cerrar
// antes de abrir, y la base exige que nunca quede ninguno.
export async function cambiarResponsable(suscripcionId, payload) {
  return datos(await apiService.patch(`/api/suscripciones/${suscripcionId}/responsable`, payload));
}

// Alta en lote: inscribir una sección entera en una operación. Todo o nada.
export async function crearSuscripcionesLote(payload) {
  return datos(await apiService.post('/api/suscripciones/lote', payload));
}

// RN-6: sólo cuotas impagas, con motivo y actor. Una cuota con pagos
// aplicados la rechaza el backend.
export async function reasignarDeuda(deudaId, payload) {
  return datos(await apiService.patch(`/api/cobranza/deudas/${deudaId}/responsable`, payload));
}

// Por qué una cuota no se toca al dar de baja, en palabras del operador.
export const MOTIVO_INTOCABLE = {
  tiene_pagos_aplicados: 'Tiene pagos aplicados',
  ya_pagada: 'Ya está pagada',
  vence_antes_de_la_baja: 'Vence antes de la baja'
};

/* --- Cobros (E3) ---------------------------------------------------------- */

// El cobro exige caja abierta y es idempotente por `idempotency_key` (RN-30):
// si la red se corta y el operador vuelve a apretar, el backend devuelve el
// cobro anterior en vez de cobrar dos veces.
export async function cobrar(payload) {
  return datos(await apiService.post('/api/cobranza/cobros', payload));
}

// Buscador de productos para el cobro — CR-E4-001, RN-34.
//
// Reusa `/api/products/search`, el buscador del catálogo heredado: no hay
// endpoint propio de cobranza para esto (artículo 1). El rol `caja` ya tiene
// `ver_productos` y `ver_precio_venta`, así que no hizo falta tocar permisos.
//
// Devuelve el precio SÓLO para mostrarlo: el que se cobra lo resuelve el
// backend contra el catálogo dentro de la transacción (RN-48, RP-9). Si acá se
// viera 120.000 y el precio cambiara un segundo después, se cobra el nuevo —
// y está bien, porque el precio del catálogo es la verdad.
export async function buscarProductos(texto, { limite = 10 } = {}) {
  const t = String(texto || '').trim();
  if (t.length < 2) return [];
  // `apiService.get` recibe los PARAMS, no una config de axios (hallazgo H-J).
  const respuesta = await apiService.get('/api/products/search', { query: t });
  const filas = Array.isArray(respuesta?.data) ? respuesta.data : [];
  return filas.slice(0, limite).map((p) => ({
    producto_id: p.producto_id,
    nombre: p.nombre,
    codigo: p.codigo_producto || p.codigo_barras || '',
    precio: Number(p.precio_unitario_resuelto ?? p.precio_venta ?? 0) || null,
    stock: Number(p.stock_disponible ?? 0)
  }));
}

/* --- Ventas diferidas (E9, D33, D34) -------------------------------------- */

// Lo que se cobró sin emitir documento fiscal. La factura que las agrupa se
// emite FUERA de este sistema: acá se listan para poder armarla, y se marcan
// para que no se facturen dos veces.
export async function listarVentasDiferidas(filtros = {}) {
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return datos(await apiService.get('/api/cobranza/ventas-diferidas', params));
}

export async function consolidarVentas(payload) {
  return datos(await apiService.post('/api/cobranza/ventas-diferidas/consolidar', payload));
}

export async function listarCobros(filtros = {}) {
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return datos(await apiService.get('/api/cobranza/cobros', params)) || [];
}

export async function obtenerCobro(id) {
  return datos(await apiService.get(`/api/cobranza/cobros/${id}`));
}

export async function anularCobro(id, payload) {
  return datos(await apiService.post(`/api/cobranza/cobros/${id}/anular`, payload));
}

// Completado explícito (RN-45). No hay worker propio: es una acción del
// operador sobre un cobro que quedó sin documento por una falla funcional.
export async function completarEmision(id) {
  return datos(await apiService.post(`/api/cobranza/cobros/${id}/completar-emision`, {}));
}

/* --- Comprobante entregable (E10, RN-58) -----------------------------------
   El documento que se le da al pagador, pedido POR COBRO y no tomado de la
   respuesta del cobro. Esa es toda la diferencia: hasta acá el número y el
   enlace del recibo salían del JSON de ese request y se perdían al recargar
   (H-AB), así que el comprobante se podía entregar una sola vez. */
export async function obtenerComprobante(cobroId) {
  return datos(await apiService.get(`/api/cobranza/cobros/${cobroId}/comprobante`));
}

/* --- Estado de cuenta (E5, CF12) -------------------------------------------
   "¿Cuánto debe este padre, sumando todos sus hijos?" (Criterio 1). Se pide
   por `cliente_id`, no por suscripción: un responsable puede tener varias. */
export async function obtenerEstadoCuenta(clienteId) {
  return datos(await apiService.get(`/api/cobranza/estado-cuenta/${clienteId}`));
}

// La clave de idempotencia la genera la PANTALLA, una vez por intento de
// cobro, y se conserva si el envío falla: es lo que hace que reintentar sea
// seguro. Generarla en el backend sería inútil — cada reintento traería una
// clave nueva y cobraría de nuevo.
export function nuevaClaveIdempotencia() {
  if (window.crypto?.randomUUID) return `cobro-${window.crypto.randomUUID()}`;
  return `cobro-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const FORMAS_PAGO = [
  { valor: 'EFECTIVO', texto: 'Efectivo' },
  { valor: 'TRANSFERENCIA', texto: 'Transferencia' },
  { valor: 'TARJETA', texto: 'Tarjeta' },
  { valor: 'CHEQUE', texto: 'Cheque' }
];

// Cómo se le explica al operador en qué quedó la emisión. El texto importa
// tanto como el estado: "pendiente" a secas hace que alguien intente arreglar
// algo que se arregla solo.
// Qué se hizo con el documento fiscal al anular — RA-7.
//
// Es la misma frase que la pantalla muestra en el momento de anular; vive acá
// para que el aviso de entonces y la fila de después digan lo MISMO. Tenerla
// duplicada en la vista era garantía de que se fueran separando.
//
// El operador no conoce «CDC», «NCE» ni «evento de cancelación»: se nombra por
// lo que le pasó a la factura.
export function textoDeResolucionFiscal(cobro) {
  const referencia = cobro?.anulacion_referencia ? ` ${cobro.anulacion_referencia}` : '';
  switch (cobro?.anulacion_instrumento) {
    case 'NOTA_CREDITO':
      return `Se emitió la nota de crédito${referencia}.`;
    case 'EVENTO_CANCELACION':
      return 'La factura quedó cancelada ante SIFEN.';
    // RN-150 (SPEC_NOTAS_DE_CREDITO). El recibo emitido se anula también en
    // facturación electrónica, que emite una constancia de anulación.
    case 'ANULACION_RECIBO':
      return `El recibo quedó anulado en facturación electrónica${
        cobro?.anulacion_referencia ? `; constancia de anulación N.º ${cobro.anulacion_referencia}` : ''}.`;
    case 'CANCEL_SEND':
      return 'La factura se retiró antes de enviarse: no llegó a emitirse.';
    case 'NINGUNO':
      return 'No había factura electrónica que anular.';
    default:
      // Cobros anulados antes de que se registrara el instrumento (CR-AF-007).
      // Se dice que no se sabe, en vez de afirmar algo sobre una factura real.
      return 'No quedó registrado qué se hizo con el documento fiscal.';
  }
}

export function estadoEmision(cobro) {
  // El RECIBO no pasa por facturación electrónica, así que ninguno de los
  // estados de abajo lo describe: `estado_emision` nace en
  // `PENDIENTE_EMISION` por default de la tabla y **nadie lo toca nunca**,
  // porque la emisión del recibo todavía no está construida (CR-E3-004,
  // bloqueada por el permiso `RECIBO_WRITE` del servicio real).
  //
  // Sin este caso, la pantalla decía «En cola de reenvío — la facturación
  // electrónica no respondió, se reenvía sola». Tres mentiras en una línea:
  // no es una factura, no hay nada encolado, y no va a reintentarse.
  // Reportado por el operador el 2026-09-03.
  if (cobro?.documento_tipo === 'RECIBO') {
    if (cobro.estado_emision === 'EMITIDO') {
      return {
        texto: cobro.recibo_numero ? `Recibo N.º ${cobro.recibo_numero}` : 'Recibo emitido',
        clase: 'vx-estado-activa',
        // «el enlace de abajo» era el panel que se quito el 2026-09-06 por
        // entregar la URL interna del servicio fiscal. La entrega ahora es
        // una sola y se nombra por su boton, para que el texto no vuelva a
        // apuntar a algo que no esta en pantalla.
        nota: 'El recibo de dinero quedó firmado. Entregáselo al pagador con '
          + '«Entregar comprobante».'
      };
    }
    if (cobro.estado_emision === 'FALLIDO') {
      // FALLIDO y no «en cola»: el recibo NO tiene worker que lo reintente
      // solo, a diferencia de la factura. Decir «se reenvía sola» sería
      // pedirle al operador que espere algo que no va a pasar.
      return {
        texto: 'Recibo sin emitir',
        clase: 'vx-estado-atencion',
        nota: 'El cobro quedó registrado y la caja está correcta, pero el recibo no '
          + 'se pudo emitir. Nadie lo reintenta solo: usá «Completar emisión» '
          + `cuando esté resuelto.${cobro.ultimo_error ? ` Motivo: ${cobro.ultimo_error}` : ''}`
      };
    }
  }

  const mapa = {
    EMITIDO: {
      texto: 'Emitido',
      clase: 'vx-estado-activa',
      nota: 'El documento electrónico se emitió correctamente.'
    },
    NO_REQUERIDO: {
      texto: 'Sin documento electrónico',
      clase: 'vx-estado-neutro',
      nota: 'La facturación electrónica está desactivada en esta instancia.'
    },
    PENDIENTE_EMISION: {
      texto: 'En cola de reenvío',
      clase: 'vx-estado-neutro',
      nota: 'La facturación electrónica no respondió. Se reenvía sola cuando vuelva: no hace falta hacer nada.'
    },
    FALLIDO: {
      texto: 'Falló la emisión',
      clase: 'vx-estado-atencion',
      nota: 'El documento no se emitió y no se reintenta solo. Completá la emisión cuando esté resuelto el problema.'
    },
    ANULADO: {
      texto: 'Anulado',
      clase: 'vx-estado-neutro',
      // **Qué pasó con la FACTURA, no sólo con el cobro.** «Anulado» a secas
      // dejaba al operador sin saber si el documento seguía vivo en SIFEN —
      // que es la única pregunta que importa cuando viene el contador—. El
      // dato ya viajaba en la fila (`SELECT c.*`), sólo no se mostraba.
      nota: 'El cobro fue anulado y sus aplicaciones revertidas. '
        + textoDeResolucionFiscal(cobro)
    }
  };
  return mapa[cobro?.estado_emision] || { texto: cobro?.estado_emision || '—', clase: 'vx-estado-neutro', nota: '' };
}

/* --- Vocabulario (RN-37) -------------------------------------------------- */

// Etiquetas configurables: "mensualidad", "cuota", "abono". Cambian lo que ve
// el operador y NADA más — ni el esquema, ni el comportamiento (articulo 2).
//
// Se cachea en memoria: es configuración que no cambia dentro de una sesión, y
// pedirla en cada pantalla serían tres viajes para leer seis palabras.
let cacheVocabulario = null;

export async function obtenerVocabulario({ recargar = false } = {}) {
  if (cacheVocabulario && !recargar) return cacheVocabulario;
  try {
    cacheVocabulario = datos(await apiService.get('/api/vocabulario'));
  } catch {
    // Si falla, la pantalla se dibuja con el vocabulario neutro. Una etiqueta
    // en su valor por defecto es un problema menor; una pantalla en blanco
    // porque no se pudo leer una palabra, no.
    cacheVocabulario = { ...VOCABULARIO_POR_DEFECTO };
  }
  return cacheVocabulario;
}

export const VOCABULARIO_POR_DEFECTO = Object.freeze({
  'suscripcion.singular': 'Suscripción',
  'suscripcion.plural': 'Suscripciones',
  'deuda.singular': 'Cuota',
  'deuda.plural': 'Cuotas',
  'ficha.singular': 'Ficha',
  'responsable.singular': 'Responsable de pago'
});

/* --- Personas ------------------------------------------------------------- */

// Los titulares son `clientes`, la tabla heredada (RN-1): no se duplica la
// persona en el dominio nuevo. Se consume el listado que ya existe, gateado
// por `ver_cliente`, que tanto `administrador` como `caja` ya tienen.
export async function buscarClientes(termino, limite = 8) {
  const respuesta = await apiService.get('/api/clients', {
    search: termino, limit: limite, page: 1
  });
  return respuesta?.data?.data ?? [];
}

/* --- Utilidades de presentación ------------------------------------------- */

export const ROLES_TITULAR = [
  { valor: 'RESPONSABLE_PAGO', texto: 'Responsable de pago' },
  { valor: 'COTITULAR', texto: 'Cotitular' },
  { valor: 'CONTACTO', texto: 'Contacto' }
];

export const rolTexto = (valor) =>
  ROLES_TITULAR.find((r) => r.valor === valor)?.texto || valor;

// D43 / RN-117.1 — quiénes pueden pagar una suscripción que no es suya.
//
// **Espejo de `ROLES_QUE_PAGAN` de `src/domain/cobros/pagador.js` en la API**,
// que es la autoridad: el servidor revalida siempre, dentro de la transacción
// y después de bloquear las deudas. Acá vive sólo para no OFRECER en el
// selector a alguien que el servidor va a rechazar — una pantalla que ofrece
// lo que el backend no acepta es peor que una que no ofrece nada.
//
// Si alguna vez aparece un rol nuevo que paga, se cambian las dos listas. Son
// dos nombres, y el precio de no duplicarlas sería exponer el criterio en el
// contrato de un endpoint compartido.
export const ROLES_QUE_PAGAN = ['RESPONSABLE_PAGO', 'COTITULAR'];

// Los titulares vigentes que pueden pagar, con el responsable primero —que es
// el orden en que el selector los ofrece y el default.
export function candidatosAPagador(titulares = []) {
  return titulares
    .filter((t) => !t.vigencia_hasta && ROLES_QUE_PAGAN.includes(t.rol))
    .sort((a, b) => {
      if (a.rol === b.rol) return Number(a.cliente_id) - Number(b.cliente_id);
      return a.rol === 'RESPONSABLE_PAGO' ? -1 : 1;
    });
}

// Guaraníes: sin decimales. `es-PY` agrupa con punto, que es lo que el
// operador espera leer.
export function formatearMonto(valor, moneda = 'PYG') {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  const decimales = moneda === 'PYG' ? 0 : 2;
  return n.toLocaleString('es-PY', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  });
}

export function iniciales(nombre) {
  return String(nombre || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || '')
    .join('')
    .toUpperCase() || '—';
}

// El documento del titular, con la etiqueta correcta. Un RUC y una cédula no
// son lo mismo y el operador necesita distinguirlos de un vistazo.
export function documentoDe(persona) {
  if (!persona) return '';
  if (persona.ruc) return `RUC ${persona.ruc}`;
  if (persona.ci) return `CI ${persona.ci}`;
  return '';
}

export function fechaCorta(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : String(iso);
}

// Con hora: un cobro se identifica por CUÁNDO se hizo, y dos del mismo día
// sólo se distinguen por la hora. `fechaCorta` no sirve acá — es para
// vencimientos, que son fechas sin hora.
export function fechaHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('es-PY');
}

/* --- Catálogos de presentación (E2) --------------------------------------- */

// Los tres tipos de IVA del régimen paraguayo, con los mismos ids que ya usa
// el resto del repo (`RegistrarProductoModal.vue`, `Factura.vue`): no se
// inventa un catálogo propio para el dominio nuevo.
export const TIPOS_IVA = [
  { valor: 1, texto: 'IVA 10%' },
  { valor: 2, texto: 'IVA 5%' },
  { valor: 3, texto: 'Exenta' }
];

export const ivaTexto = (valor) =>
  TIPOS_IVA.find((t) => t.valor === Number(valor))?.texto || '—';

// Presets del modo CALENDARIO. El modo DIAS no usa preset: cuenta ciclos de N
// días (D19), y por eso la UI muestra un campo y no una lista.
export const PRESETS_PERIODICIDAD = [
  { valor: 'MENSUAL', texto: 'Mensual', meses: 1 },
  { valor: 'BIMESTRAL', texto: 'Bimestral', meses: 2 },
  { valor: 'TRIMESTRAL', texto: 'Trimestral', meses: 3 },
  { valor: 'SEMESTRAL', texto: 'Semestral', meses: 6 },
  { valor: 'ANUAL', texto: 'Anual', meses: 12 }
];

export const presetTexto = (valor) =>
  PRESETS_PERIODICIDAD.find((p) => p.valor === valor)?.texto || valor || '—';

// Lo que el ESQUEMA deja crear. `EVENTUAL` no está acá a propósito (PC-7):
// `POST /api/esquemas/:id/conceptos` lo rechaza con 422, así que ofrecerlo en
// este selector sería ofrecer un botón que falla. El cargo eventual se crea
// desde su propia pantalla, con su propio permiso (D38).
export const TIPOS_CONCEPTO = [
  { valor: 'RECURRENTE', texto: 'Recurrente', nota: 'Se cobra en todos los períodos' },
  { valor: 'UNICO', texto: 'Único', nota: 'Sólo en el primer período — matrícula, inscripción' }
];

// Cómo se LEE cada tipo, que es distinto de cuáles se pueden crear acá. Un
// cargo eventual aparece en el listado del esquema aunque no se haya creado
// desde ahí: sin esta entrada se vería el literal «EVENTUAL» en la pantalla.
const TIPOS_CONCEPTO_LECTURA = [
  ...TIPOS_CONCEPTO,
  { valor: 'EVENTUAL', texto: 'Cargo eventual', nota: 'Se emite a mano, a quien corresponda' }
];

export const tipoConceptoTexto = (valor) =>
  TIPOS_CONCEPTO_LECTURA.find((t) => t.valor === valor)?.texto || valor;

// ---------------------------------------------------------------------------
// Cargo eventual (CE-F7) — SPEC_CARGO_EVENTUAL_v0.1.md, D38
// ---------------------------------------------------------------------------
//
// Grupo propio bajo `/api/cobranza/cargos`, y NO bajo `/api/esquemas/*`: ese
// prefijo es del administrador y del calendario. Las siete van con
// `administrar_cargos_eventuales`, la de elegibles incluida — si el padrón
// saliera de `/api/suscripciones` habría que darle `ver_suscripciones` al
// gerente, y el permiso «sólo esto» dejaría de ser sólo esto (PC-3).

export async function listarCargos(esquemaId) {
  return datos(await apiService.get('/api/cobranza/cargos',
    { cr_esquema_id: esquemaId })) || [];
}

export async function crearCargo(payload) {
  return datos(await apiService.post('/api/cobranza/cargos', payload));
}

export async function actualizarCargo(conceptoId, payload) {
  return datos(await apiService.patch(`/api/cobranza/cargos/${conceptoId}`, payload));
}

export async function desactivarCargo(conceptoId) {
  return datos(await apiService.delete(`/api/cobranza/cargos/${conceptoId}`));
}

// El padrón activo del esquema, con `ya_lo_tiene` y `sin_responsable`. Vienen
// TODOS y no filtrados: el operador tiene que poder ver que a esos dos ya se
// les emitió, no que desaparezcan de la lista sin explicación.
export async function elegiblesParaCargo(conceptoId) {
  return datos(await apiService.get(`/api/cobranza/cargos/${conceptoId}/elegibles`));
}

export async function previsualizarCargo(conceptoId, payload) {
  return datos(await apiService.post(
    `/api/cobranza/cargos/${conceptoId}/previsualizar`, payload));
}

export async function emitirCargo(conceptoId, payload) {
  return datos(await apiService.post(`/api/cobranza/cargos/${conceptoId}/emitir`, payload));
}

// RN-65. Sólo acepta un cargo eventual sin pagos aplicados. Si ya se cobró, el
// camino es anular el COBRO, que además resuelve el documento fiscal.
export async function anularCargoEmitido(deudaId, motivo) {
  return datos(await apiService.post(`/api/cobranza/deudas/${deudaId}/anular`, { motivo }));
}

export const TIPOS_DESCUENTO = [
  { valor: 'PORCENTAJE', texto: 'Porcentaje' },
  { valor: 'MONTO_FIJO', texto: 'Monto fijo' }
];

// Cómo se lee un descuento en una línea: "Beca hermanos — 15%".
export function descuentoTexto(d) {
  if (!d) return '';
  return d.tipo === 'PORCENTAJE'
    ? `${Number(d.valor)}%`
    : formatearMonto(d.valor);
}

// Cómo se describe la periodicidad de un esquema en una sola línea.
export function periodicidadTexto(esquema) {
  if (!esquema) return '—';
  if (esquema.periodicidad_tipo === 'DIAS') {
    return `Cada ${esquema.periodicidad_dias} días · vence a los ${esquema.dias_hasta_vencimiento}`;
  }
  return `${presetTexto(esquema.periodicidad_preset)} · vence el ${esquema.dia_vencimiento}`;
}

// El estado de una deuda para el operador. `vencida` NO es una columna: la
// deriva el backend (RN-19), y acá pesa más que el estado —una PENDIENTE
// vencida es lo que hay que mirar primero.
export function estadoDeuda(deuda) {
  if (!deuda) return { texto: '—', clase: 'vx-estado-neutro' };
  if (deuda.estado === 'ANULADA') return { texto: 'Anulada', clase: 'vx-estado-neutro' };
  if (deuda.estado === 'PAGADA') return { texto: 'Pagada', clase: 'vx-estado-activa' };
  if (deuda.vencida) return { texto: 'Vencida', clase: 'vx-estado-atencion' };
  if (Number(deuda.saldo) < Number(deuda.monto_total)) {
    return { texto: 'Parcial', clase: 'vx-estado-neutro' };
  }
  return { texto: 'Pendiente', clase: 'vx-estado-neutro' };
}
